import { isRejectedWithValue, type Middleware } from '@reduxjs/toolkit';
import { rsosApi } from '@rsos/reduxDataStore/src/features/rsosApi';
import { captureExceptionWithScope } from '@rsos/utils/sentry';

/**
 * Reads the HTTP status from an RTK Query rejection payload.
 *
 * fetchBaseQuery HTTP failures carry a numeric status. fetchBaseQuery's other
 * failure modes ship a string status: 'FETCH_ERROR' (network), 'PARSING_ERROR'
 * (JSON parse), 'TIMEOUT_ERROR', 'CUSTOM_ERROR' (queryFn rejection).
 * Throws inside `transformResponse` / `queryFn` / `prepareHeaders` are wrapped
 * by RTK Query's thunk and surface as the thrown value itself (e.g. a ZodError)
 * with no `status` field; the caller handles that case via the undefined return.
 */
function getStatus(payload: unknown): number | string | undefined {
  if (payload && typeof payload === 'object' && 'status' in payload) {
    const status = (payload as { status: unknown }).status;
    if (typeof status === 'number' || typeof status === 'string') return status;
  }
  return undefined;
}

interface RTKQueryRejectionMeta {
  arg?: {
    endpointName?: string;
    originalArgs?: unknown;
  };
  baseQueryMeta?: {
    request?: { url?: string };
  };
}

/**
 * Catches every RTK Query mutation/query rejection from rsosApi (and its
 * injectEndpoints extensions) and reports the failure to Sentry as a captured
 * exception with the request context attached as `extra`. Reporting only — no
 * operator-facing toast.
 *
 * Scoped to rsosApi only via an action-type prefix check; standalone
 * createApi instances (transcriptionApi, displaySchemaApi, radiosApi,
 * getSuggestedAddressesApi, transcriptFeedbackApi) are unaffected by this PR.
 *
 * ## Opting out specific status codes
 *
 * If the backend legitimately returns an error code as a normal state for a
 * particular endpoint (e.g. a 404 that means "no record exists yet"), tag the
 * rejected payload with `isExpectedError: true` from that endpoint's
 * `transformErrorResponse`:
 *
 *     transformErrorResponse: error =>
 *       typeof error.status === 'number' && error.status === 404
 *         ? { ...error, isExpectedError: true }
 *         : error,
 *
 * The consumer still receives the rejection (so the hook's `error` field
 * works normally), but this middleware passes it through without logging it
 * to Sentry.
 *
 * Note: `transformErrorResponse` only runs on errors that flow out of the
 * baseQuery (HTTP failures, fetch/timeout errors). Throws inside
 * `transformResponse` (e.g. zod schema parse failures on a 200) take a
 * different path and still log — which is what we want, since a schema
 * mismatch is a real bug.
 */
export const rtkQueryErrorMiddleware: Middleware = () => next => action => {
  if (isRejectedWithValue(action)) {
    // NOTE: We're only reporting errors on the rsosApi-derived APIs. There are
    //   other RTK Query APIs that don't extend rsosApi (e.g. transcriptionApi)
    //   that we deemed out of scope to limit blast radius of this change.
    if (!String(action.type).startsWith(`${rsosApi.reducerPath}/`)) {
      return next(action);
    }

    if (
      action.payload &&
      typeof action.payload === 'object' &&
      'isExpectedError' in action.payload &&
      (action.payload as { isExpectedError?: unknown }).isExpectedError === true
    ) {
      return next(action);
    }

    const meta = (action.meta ?? {}) as RTKQueryRejectionMeta;
    const endpointName = meta.arg?.endpointName ?? 'unknown';
    const url = meta.baseQueryMeta?.request?.url ?? '';
    const status = getStatus(action.payload);

    // 401s are owned by baseQueryWithReauth in rsosApi: it refreshes the
    // access/product token and, if that fails, clears auth storage and logs
    // the user out. Reporting them here too is noise for what is a normal
    // session-expiry path.
    if (status === 401) {
      return next(action);
    }

    // Capture as a real exception (not console.error): the message becomes the
    // Sentry issue title verbatim, instead of captureConsoleIntegration
    // appending a "[object Object]" stringification of the context object. The
    // fingerprint keeps each endpoint/status in its own issue, since synthetic
    // exceptions captured from this single line would otherwise all group by
    // stack trace.
    captureExceptionWithScope(
      new Error(
        `[unhandled RTK Query error] ${endpointName} ${status ?? 'error'}`,
      ),
      {
        endpointName,
        status,
        url,
        payload: action.payload,
        args: meta.arg?.originalArgs,
      },
      ['rtk-query-error', endpointName, String(status ?? 'error')],
    );
  }
  return next(action);
};
